import React, { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  AppBar,
  Toolbar,
  Grow,
  Fade,
  Slide,
  Zoom,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@store/index'
import { setLoading, setError } from '@store/slices/authSlice'
import { addNotification } from '@store/slices/appSlice'
import api from '@services/api'
import {
  createValidationRules,
  validateForm,
  calculatePasswordStrength,
  PasswordStrength,
} from '@utils/validation'
import PasswordStrengthMeter from '@components/ui/PasswordStrengthMeter'

/**
 * Determines the user role based on the current route path
 */
const getUserRoleFromPath = (pathname: string): 'patient' | 'therapist' => {
  if (pathname.includes('/therapist-reset-password')) {
    return 'therapist'
  }
  if (pathname.includes('/patient-reset-password')) {
    return 'patient'
  }
  // Default to patient if cannot determine
  return 'patient'
}

/**
 * Gets the role-based primary color
 */
const getRoleColor = (role: 'patient' | 'therapist'): string => {
  return role === 'therapist' ? '#2E7DAF' : '#3BAE78'
}

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const dispatch = useAppDispatch()
  const { error, isLoading } = useAppSelector(state => state.auth)

  // Extract token from URL query parameter
  const token = searchParams.get('token')

  // Determine user role from route path
  const userRole = getUserRoleFromPath(location.pathname)
  const primaryColor = getRoleColor(userRole)

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>(
    calculatePasswordStrength('')
  )
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [success, setSuccess] = useState(false)
  const [tokenError, setTokenError] = useState<string | null>(
    !token
      ? 'Invalid or missing reset token. Please request a new password reset link.'
      : null
  )

  const validationRules = {
    newPassword: [
      createValidationRules.required('Password is required'),
      createValidationRules.minLength(
        8,
        'Password must be at least 8 characters'
      ),
    ],
    confirmPassword: [
      createValidationRules.required('Please confirm your password'),
    ],
  }

  const handleInputChange =
    (field: 'newPassword' | 'confirmPassword') =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }))

      // Clear field error when user starts typing
      if (formErrors[field]) {
        setFormErrors(prev => ({
          ...prev,
          [field]: '',
        }))
      }

      // Calculate password strength when newPassword field changes
      if (field === 'newPassword') {
        setPasswordStrength(calculatePasswordStrength(value))
      }

      // Clear confirm password error if passwords match
      if (field === 'confirmPassword' && formErrors.confirmPassword) {
        if (value === formData.newPassword) {
          setFormErrors(prev => ({
            ...prev,
            confirmPassword: '',
          }))
        }
      }
    }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    // Check if token exists
    if (!token) {
      setTokenError(
        'Invalid or missing reset token. Please request a new password reset link.'
      )
      return
    }

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setFormErrors({
        confirmPassword: 'Passwords do not match',
      })
      return
    }

    // Validate form
    const errors = validateForm(formData, validationRules)
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }

    dispatch(setLoading(true))
    dispatch(setError(null))
    setSuccess(false)
    setTokenError(null)

    try {
      const endpoint =
        userRole === 'therapist'
          ? '/therapist/reset-password'
          : '/patient/reset-password'

      await api.post(`${endpoint}?token=${token}`, {
        newPassword: formData.newPassword,
        token: token,
      })

      setSuccess(true)
      dispatch(
        addNotification({
          type: 'success',
          title: 'Password Reset',
          message: 'Your password has been successfully reset.',
        })
      )

      // Redirect to appropriate login page based on user role
      setTimeout(() => {
        const loginPath =
          userRole === 'therapist' ? '/provider-login' : '/patient-login'
        navigate(loginPath)
      }, 2000)
    } catch (error: any) {
      console.error('Reset password failed:', error)
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to reset password. Please try again.'
      dispatch(setError(errorMessage))
    } finally {
      dispatch(setLoading(false))
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Fade in timeout={800}>
        <AppBar
          position="static"
          elevation={0}
          sx={{
            bgcolor: 'white',
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            height: '80px',
            flexShrink: 0,
          }}
        >
          <Toolbar
            sx={{ justifyContent: 'space-between', py: 1, height: '100%' }}
          >
            <Slide direction="right" in timeout={1000}>
              <Box
                component="img"
                src="/logo.svg"
                alt="PillarThera Logo"
                sx={{
                  height: { xs: 32, sm: 60 },
                  width: 'auto',
                  cursor: 'pointer',
                  transition: 'opacity 0.3s ease',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
                onClick={() => navigate('/')}
              />
            </Slide>
          </Toolbar>
        </AppBar>
      </Fade>

      {/* Main Content */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Left Section - Image */}
        <Zoom in timeout={1200} style={{ transitionDelay: '300ms' }}>
          <Box
            sx={{
              flex: 1,
              position: 'relative',
              display: { xs: 'none', md: 'block' },
              overflow: 'hidden',
              bgcolor: 'grey.50',
              paddingLeft: '3rem',
              paddingTop: '1rem',
            }}
          >
            <Box
              component="img"
              src="/Group_6.png"
              alt="Reset password illustration"
              sx={{
                width: '90%',
                height: '90%',
                objectFit: 'cover',
                objectPosition: 'center',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.02)',
                },
              }}
            />
          </Box>
        </Zoom>

        {/* Right Section - Reset Password Form */}
        <Slide
          direction="left"
          in
          timeout={1000}
          style={{ transitionDelay: '500ms' }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              px: { xs: 3, sm: 4, md: 6, lg: 8 },
              py: { xs: 4, sm: 6 },
              bgcolor: 'grey.50',
            }}
          >
            {/* Reset Password Form Card */}
            <Grow in timeout={1200} style={{ transitionDelay: '700ms' }}>
              <Paper
                elevation={2}
                sx={{
                  p: { xs: 4, sm: 5, md: 6 },
                  borderRadius: 4,
                  width: '100%',
                  maxWidth: { xs: '100%', sm: '450px', md: '500px' },
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                {/* Header */}
                <Fade in timeout={1000} style={{ transitionDelay: '900ms' }}>
                  <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        color: 'grey.800',
                        mb: 1,
                        fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
                      }}
                    >
                      Reset Password
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'grey.600',
                        fontWeight: 400,
                        fontSize: { xs: '1rem', sm: '1.125rem' },
                      }}
                    >
                      {success
                        ? 'Your password has been reset successfully'
                        : 'Enter your new password below'}
                    </Typography>
                  </Box>
                </Fade>

                {/* Token Error Display */}
                {tokenError && (
                  <Fade in timeout={800} style={{ transitionDelay: '1000ms' }}>
                    <Alert
                      severity="error"
                      sx={{ mb: 3 }}
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          onClick={() =>
                            navigate(
                              userRole === 'therapist'
                                ? '/forgot-password?role=therapist'
                                : '/forgot-password?role=patient'
                            )
                          }
                          sx={{ textTransform: 'none' }}
                        >
                          Request New Link
                        </Button>
                      }
                    >
                      {tokenError}
                    </Alert>
                  </Fade>
                )}

                {/* Success Display */}
                {success && (
                  <Fade in timeout={800} style={{ transitionDelay: '1000ms' }}>
                    <Alert severity="success" sx={{ mb: 3 }}>
                      Your password has been reset successfully! You can now log
                      in using your new password.
                    </Alert>
                  </Fade>
                )}

                {/* Error Display */}
                {error && !success && !tokenError && (
                  <Fade in timeout={800} style={{ transitionDelay: '1000ms' }}>
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  </Fade>
                )}

                {/* Reset Password Form */}
                {!success && !tokenError && (
                  <Box component="form" onSubmit={handleSubmit}>
                    {/* New Password Field */}
                    <Fade
                      in
                      timeout={800}
                      style={{ transitionDelay: '1100ms' }}
                    >
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'grey.700',
                            mb: 1,
                            fontWeight: 500,
                            fontSize: '0.875rem',
                          }}
                        >
                          New Password
                        </Typography>
                        <TextField
                          fullWidth
                          type={showPassword ? 'text' : 'password'}
                          value={formData.newPassword}
                          onChange={handleInputChange('newPassword')}
                          placeholder="Enter new password"
                          required
                          error={!!formErrors.newPassword}
                          helperText={formErrors.newPassword}
                          disabled={isLoading}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'white',
                              borderRadius: 2,
                              transition: 'all 0.3s ease',
                              '& fieldset': {
                                borderColor:
                                  formData.newPassword.length > 0
                                    ? passwordStrength.borderColor
                                    : 'grey.300',
                              },
                              '&:hover fieldset': {
                                borderColor:
                                  formData.newPassword.length > 0
                                    ? passwordStrength.borderColor
                                    : 'grey.400',
                                transform: 'scale(1.02)',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: passwordStrength.borderColor,
                                borderWidth: 2,
                              },
                            },
                            '& .MuiInputBase-input': {
                              py: 1.5,
                              fontSize: '0.875rem',
                            },
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowPassword(!showPassword)}
                                  edge="end"
                                  disabled={isLoading}
                                  sx={{
                                    color: 'grey.400',
                                    transition: 'transform 0.2s ease',
                                    '&:hover': {
                                      transform: 'scale(1.1)',
                                    },
                                  }}
                                >
                                  {showPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        {formData.newPassword.length > 0 && (
                          <PasswordStrengthMeter strength={passwordStrength} />
                        )}
                      </Box>
                    </Fade>

                    {/* Confirm Password Field */}
                    <Fade
                      in
                      timeout={800}
                      style={{ transitionDelay: '1300ms' }}
                    >
                      <Box sx={{ mb: 4 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'grey.700',
                            mb: 1,
                            fontWeight: 500,
                            fontSize: '0.875rem',
                          }}
                        >
                          Confirm New Password
                        </Typography>
                        <TextField
                          fullWidth
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={formData.confirmPassword}
                          onChange={handleInputChange('confirmPassword')}
                          placeholder="Confirm new password"
                          required
                          error={!!formErrors.confirmPassword}
                          helperText={formErrors.confirmPassword}
                          disabled={isLoading}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'white',
                              borderRadius: 2,
                              transition: 'all 0.3s ease',
                              '& fieldset': {
                                borderColor: 'grey.300',
                              },
                              '&:hover fieldset': {
                                borderColor: 'grey.400',
                                transform: 'scale(1.02)',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: primaryColor,
                                borderWidth: 2,
                              },
                            },
                            '& .MuiInputBase-input': {
                              py: 1.5,
                              fontSize: '0.875rem',
                            },
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() =>
                                    setShowConfirmPassword(!showConfirmPassword)
                                  }
                                  edge="end"
                                  disabled={isLoading}
                                  sx={{
                                    color: 'grey.400',
                                    transition: 'transform 0.2s ease',
                                    '&:hover': {
                                      transform: 'scale(1.1)',
                                    },
                                  }}
                                >
                                  {showConfirmPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                    </Fade>

                    {/* Submit Button */}
                    <Fade
                      in
                      timeout={800}
                      style={{ transitionDelay: '1500ms' }}
                    >
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={isLoading}
                        sx={{
                          py: 1.5,
                          fontSize: '1rem',
                          fontWeight: 600,
                          borderRadius: 2,
                          mb: 3,
                          textTransform: 'none',
                          transition: 'all 0.3s ease',
                          bgcolor: primaryColor,
                          '&:hover': {
                            bgcolor: primaryColor,
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                            opacity: 0.9,
                          },
                          '&:disabled': {
                            bgcolor: 'grey.300',
                            color: 'grey.500',
                          },
                        }}
                      >
                        {isLoading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          'Reset Password'
                        )}
                      </Button>
                    </Fade>
                  </Box>
                )}
              </Paper>
            </Grow>
          </Box>
        </Slide>
      </Box>
    </Box>
  )
}

export default ResetPasswordPage
